import { parseYouTubeVideoId } from '@/util/util_youtube';

// The hero video field holds one of two things: a YouTube link, or the download
// URL of a video uploaded to Firebase Storage. They play very differently — one
// is an iframe we can only ask nicely, the other a `<video>` tag we control
// completely — so which one it is gets decided here, once, rather than at each
// place that has to render or validate it.

export type HeroVideoSourceType =
  | { kind: 'youtube'; videoId: string }
  | { kind: 'file'; url: string };

/**
 * Hero video formats we accept, each mapped to the extension its stored object
 * must carry.
 *
 * One map rather than two lists, because the two ends of the trip read
 * different halves of it: the upload gate matches a picked file against the
 * keys, and `isVideoFileUrl` recognises a stored URL by the values. Split into
 * separate constants they drift, and a format accepted on the way in but
 * unrecognised on the way out fails silently — the upload finishes, the URL is
 * saved, and the video simply never appears.
 *
 * Kept narrow to what every browser we support can play from a plain `<video>`
 * tag: MP4 (H.264) is the one universal answer and WebM covers Chrome and
 * Firefox. QuickTime is deliberately absent — a .mov plays in Safari and
 * nowhere else, so an editor on Chrome would upload one, see a blank preview,
 * and ship a hero that shows the still image to most of the audience. A .mov
 * off a phone or a camera needs converting to MP4 first.
 */
export const VIDEO_TYPE_EXTENSIONS: Record<string, string> = {
  'video/mp4': '.mp4',
  'video/webm': '.webm',
};

// Matched on the path rather than the host: a Firebase download URL carries the
// object's name (extension included) inside its percent-encoded path, and this
// also accepts a file served from anywhere else the site might move to.
const VIDEO_FILE_EXTENSIONS = Object.values(VIDEO_TYPE_EXTENSIONS);

function isVideoFileUrl(input: string): boolean {
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return false;
  }
  // https only — a video loaded over http would break the page's secure context.
  if (url.protocol !== 'https:') return false;

  let path: string;
  try {
    path = decodeURIComponent(url.pathname).toLowerCase();
  } catch {
    // Malformed percent-encoding; not a URL we produced.
    return false;
  }
  return VIDEO_FILE_EXTENSIONS.some((extension) => path.endsWith(extension));
}

/**
 * Works out how a stored hero video URL should be played, or returns null if it
 * is neither kind — an empty field, or something stale like a Vimeo link — in
 * which case the hero falls back to its image rather than showing a dead player.
 */
export function parseHeroVideoSource(input: string): HeroVideoSourceType | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const videoId = parseYouTubeVideoId(trimmed);
  if (videoId) return { kind: 'youtube', videoId };

  return isVideoFileUrl(trimmed) ? { kind: 'file', url: trimmed } : null;
}
